import { motion } from 'framer-motion';
import { SignIn, useAuth } from "@clerk/clerk-react";
import { useNavigate } from 'react-router-dom';
import Spline from '@splinetool/react-spline';
import { useState, useEffect } from 'react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const [splineError, setSplineError] = useState(false);

  useEffect(() => {
    if (isSignedIn) {
      navigate('/upload');
    }
  }, [isSignedIn, navigate]);

  useEffect(() => {
    const preventDefault = (e: TouchEvent | WheelEvent) => {
      if (e instanceof WheelEvent && e.ctrlKey) {
        e.preventDefault();
      }
      if (e instanceof TouchEvent && e.touches.length > 1) {
        e.preventDefault();
      }
    };

    document.addEventListener('wheel', preventDefault, { passive: false });
    document.addEventListener('touchstart', preventDefault, { passive: false });

    return () => {
      document.removeEventListener('wheel', preventDefault);
      document.removeEventListener('touchstart', preventDefault);
    };
  }, []);

  const onSplineError = (e: any) => {
    console.error('Spline error:', e);
    setSplineError(true);
  };

  const onSplineLoad = (spline: any) => {
    if (spline) {
      const camera = spline.camera;
      if (camera && camera.controls) {
        camera.controls.enabled = false;
        camera.controls.enableZoom = false;
        camera.controls.enablePan = false;
        camera.controls.enableRotate = false;
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top section with Spline animation */}
      <div className="relative h-[45vh] md:h-[55vh] bg-[#8364FF]/[0.07] overflow-hidden">
        {!splineError ? (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 scale-[0.38] sm:scale-[0.47] md:scale-[0.51] lg:scale-[0.55] xl:scale-[0.6]">
            <Spline 
              scene="https://prod.spline.design/Jq9y3GwQ8hS7MeAd/scene.splinecode"
              onLoad={onSplineLoad}
              onError={onSplineError}
              style={{
                width: '200vw',
                height: '200vh',
                backgroundColor: 'transparent',
                touchAction: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                pointerEvents: 'none',
              }}
            />
          </div>
        ) : (
          <motion.div
            className="w-48 h-48"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative w-full h-full">
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-12 h-12 bg-purple-main/20 rounded-full"
                  animate={{
                    x: Math.sin(i * Math.PI / 4) * 60,
                    y: Math.cos(i * Math.PI / 4) * 60,
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.2
                  }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Bottom section with text and sign-in */}
      <div className="flex-1 flex flex-col items-center px-6 py-8 md:py-12 max-w-md mx-auto w-full relative z-10 bg-white">
        <h1 className="text-[28px] md:text-[36px] font-bold text-[#221A51] leading-tight mb-6 text-left w-full">
          Gain Insights Into Your Spending Habits
        </h1>
        
        <div className="w-full">
          <style>{`
            .auth-container {
              background: transparent !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              overflow: visible !important;
              border: none !important;
              box-shadow: none !important;
            }
            
            .auth-container > div {
              background: transparent !important;
              box-shadow: none !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              overflow: visible !important;
              border: none !important;
            }

            .auth-button {
              width: 100% !important;
              height: 48px !important;
              border: 1px solid rgba(34, 26, 81, 0.1) !important;
              border-radius: 100px !important;
              font-size: 16px !important;
              font-weight: 500 !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              gap: 12px !important;
              cursor: pointer !important;
              transform: translate(0, 0) !important;
              transition: all 0.1s ease !important;
              margin-bottom: 16px !important;
              background: white !important;
              color: #221A51 !important;
              padding: 0 24px !important;
              box-shadow: 0 2px 4px rgba(34, 26, 81, 0.05) !important;
            }

            .auth-button:hover {
              transform: translate(-2px, -2px) !important;
              box-shadow: 4px 4px 0 0 #221A51 !important;
              border: 2px solid #221A51 !important;
              padding: 0 23px !important;
            }

            .auth-button:active {
              transform: translate(2px, 2px) !important;
              box-shadow: 2px 2px 0 0 #221A51 !important;
            }

            /* Apple button styles */
            .cl-card .cl-socialButtonsBlockButton[data-provider="apple"],
            .cl-socialButtonsBlockButton[data-provider="apple"].auth-button,
            button.cl-socialButtonsBlockButton[data-provider="apple"] {
              background-color: black !important;
              background: black !important;
              border-color: black !important;
              color: white !important;
            }

            .cl-card .cl-socialButtonsBlockButton[data-provider="apple"]:hover,
            .cl-socialButtonsBlockButton[data-provider="apple"].auth-button:hover,
            button.cl-socialButtonsBlockButton[data-provider="apple"]:hover {
              background-color: black !important;
              background: black !important;
              border: 2px solid #221A51 !important;
              color: white !important;
            }

            .cl-card .cl-socialButtonsBlockButton[data-provider="apple"] svg,
            .cl-socialButtonsBlockButton[data-provider="apple"].auth-button svg,
            button.cl-socialButtonsBlockButton[data-provider="apple"] svg {
              fill: white !important;
            }

            .cl-card .cl-socialButtonsBlockButton[data-provider="apple"] span,
            .cl-socialButtonsBlockButton[data-provider="apple"].auth-button span,
            button.cl-socialButtonsBlockButton[data-provider="apple"] span {
              color: white !important;
            }

            .auth-button.continue {
              background: #B4A5FF !important;
              border: 2px solid #221A51 !important;
              box-shadow: 4px 4px 0 0 #221A51 !important;
              margin-top: 16px !important;
              transform: translate(-2px, -2px) !important;
              margin-bottom: 0 !important;
              height: 40px !important;
              width: 160px !important;
              margin-left: 0 !important;
            }

            .auth-button.continue:hover {
              box-shadow: none !important;
              transform: translate(0, 0) !important;
            }

            .auth-input {
              width: 100% !important;
              height: 52px !important;
              border: 1px solid rgba(34, 26, 81, 0.1) !important;
              border-radius: 100px !important;
              padding: 0 24px !important;
              font-size: 16px !important;
              background: white !important;
              transform: translate(0, 0) !important;
              transition: all 0.1s ease !important;
              box-shadow: 0 2px 4px rgba(34, 26, 81, 0.05) !important;
            }

            .auth-input:focus {
              outline: none !important;
              border: 1px solid rgba(34, 26, 81, 0.3) !important;
            }

            .auth-divider {
              width: 100% !important;
              display: flex !important;
              align-items: center !important;
              gap: 16px !important;
              margin: 32px 0 !important;
              color: rgba(34, 26, 81, 0.4) !important;
              overflow: visible !important;
            }

            .auth-divider::before,
            .auth-divider::after {
              content: "" !important;
              flex: 1 !important;
              height: 1px !important;
              background: rgba(34, 26, 81, 0.1) !important;
            }

            /* Fix label styling */
            .cl-formFieldLabel {
              color: #221A51 !important;
              font-size: 16px !important;
              font-weight: 500 !important;
              margin-bottom: 8px !important;
              width: 100% !important;
            }

            /* Fix input placeholder */
            .auth-input::placeholder {
              color: rgba(34, 26, 81, 0.4) !important;
            }

            /* Fix form field container */
            .cl-formField {
              width: 100% !important;
              overflow: visible !important;
            }

            /* Remove any remaining borders */
            .cl-card {
              border: none !important;
              box-shadow: none !important;
            }

            .cl-internal-b3fm6y {
              border: none !important;
              box-shadow: none !important;
            }

            /* Fix social buttons container */
            .cl-socialButtonsBlock {
              margin: 0 !important;
              padding: 0 !important;
            }
          `}</style>
          <div className="text-left mb-4 text-[#221A51]/80 font-medium">
            Sign Up or Login
          </div>
          <SignIn 
            appearance={{
              baseTheme: null,
              layout: {
                socialButtonsPlacement: "top",
                socialButtonsVariant: "blockButton",
                shimmer: false,
              },
              elements: {
                rootBox: "auth-container",
                card: "auth-container",
                socialButtonsBlockButton: "auth-button",
                socialButtonsBlockButtonArrow: "hidden",
                formButtonPrimary: "auth-button continue",
                formFieldInput: "auth-input",
                dividerRow: "auth-divider",
                dividerText: "",
                dividerLine: "hidden",
                footer: "hidden",
                header: "hidden",
                headerTitle: "hidden",
                headerSubtitle: "hidden",
                formHeaderTitle: "hidden",
                formHeaderSubtitle: "hidden",
                identityPreviewEditButton: "hidden"
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
